/**
 * @file PrivateChatList.js
 * @author Simon Tenedero, Yevheniia Bazhmaieva
 * @created 2025-05-26
 * @lastModified 2025-05-27
 * @description file containing PrivateChatList component
 */

import { leavePrivateChat } from "../../utils/privateChatUtils";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useUser } from "../../context/UserContext";
import { useState } from "react";

/**
 * This component displays a list of the users' privateChats - it is the 'messages' pane displayed when selected from the private tabs
 * Each tab has an image for the chat, the title, and a recently sent message (deciding if we will include the name of the livestreams)
 *
 * @param {Object} props - The component properties
 * @param {Array} props.privateChats - Array of chat objects, each with an id, name, and url.
 * @param {Function} props.setSelectedPrivateChat - Callback function to handle chat selection.
 * @param {Array} props.privateChatMembers - Map where key-val pairs are privateChatId and its members
 * @param {Function} props.setSelectedPrivateChatMembers - Function to set the selected private chat members.
 * @param {Array} props.pendingPrivateChatMembers - Array of pending private chat members.
 * @param {Function} props.setSelectedPrivateChatPendingMembers - Function to set the selected pending private chat members.
 *
 * @returns {JSX.Element} The rendered PrivateChatList component
 */
const PrivateChatList = ({
  privateChats,
  setSelectedPrivateChat,
  privateChatMembers,
  setSelectedPrivateChatMembers,
  pendingPrivateChatMembers,
  setSelectedPrivateChatPendingMembers,
}) => {
  const [loading, setLoading] = useState(false);

  const leaveChat = async (chatId) => {
    setLoading(true); // Set loading state to true while processing deletion
    await leavePrivateChat(chatId);
    setLoading(false);
  };

  const onSelectChat = (chatId) => {
    const currentChat = privateChats.find((chat) => chat.id === chatId);
    if (!currentChat) {
      console.error("Chat not found:", chatId);
      setSelectedPrivateChat(null);
      return;
    }
    setSelectedPrivateChat(currentChat);
    setSelectedPrivateChatMembers(privateChatMembers.get(chatId) || []);
    setSelectedPrivateChatPendingMembers(
      pendingPrivateChatMembers.get(chatId) || [],
    );
    console.log("currentChat = ", currentChat);
  };

  if (privateChats.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-center p-4">
        <div className="text-4xl mb-2">🤝</div>
        <div className="text-sm text-gray-400 mb-1">
          No current privateChats
        </div>
        <div className="text-xs text-gray-500">
          Why not invite some friends?
        </div>
      </div>
    );
  }

  // If loading, show a spinner
  if (loading) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        {/* Loading spinner while settings are being loaded */}
        <span className="loading loading-spinner loading-lg text-primary"></span>
        <span className="ml-2 text-lg text-base-content">Deleting Chat...</span>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {privateChats.map((chat) => (
        <div
          key={chat.id}
          className="flex items-center w-full pl-6 pr-2 pb-1 hover:bg-base-100"
          onClick={() => {
            onSelectChat(chat.id);
          }}
        >
          {chat.icon ? (
            <img
              src={chat.icon}
              alt="Chat Icon Preview"
              className="w-11 h-11 mr-4 rounded-full object-cover border border-gray-300 shadow-sm flex-shrink-0"
            />
          ) : (
            <div className="w-11 h-11 mr-4 rounded-full bg-gray-300 border border-gray-300 shadow-sm flex items-center justify-center flex-shrink-0">
              <span className="text-2xl font-bold text-gray-600">
                {chat.name ? chat.name.charAt(0).toUpperCase() : "?"}
              </span>
            </div>
          )}
          <div className="flex flex-wrap items-end p-2 cursor-pointer text-xs sm:text-sm flex-grow">
            <p className="font-bold cursor-pointer w-[125px] md:w-[150px] min-w-0 whitespace-nowrap overflow-hidden text-ellipsis block text-sm">
              {chat.name || chat.url}
            </p>

            {/* create a menu beside each chat so users can leave the chat if neeeded - will call on leaveChat to handle db functions*/}
            <Menu as="div" className="ml-auto relative inline-block">
              {({ open }) => (
                <div onClick={(e) => e.stopPropagation()}>
                  <MenuButton
                    className={`relative w-8 h-8 font-bold ${open ? "bg-base-200 rounded-full" : ""}`}
                  >
                    ⋮
                  </MenuButton>

                  {open && (
                    <MenuItems className="absolute top-full right-6 mt-1 w-20 bg-base-200 rounded-full">
                      <MenuItem
                        className={`right-3 w-content bg-base-200 text-white rounded-full hover:bg-primary text-sm`}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); //stop event bubbling - which would trigger parent onClick
                            leaveChat(chat.id);
                          }}
                          className="whitespace-nowrap px-5 py-1"
                        >
                          leave chat
                        </button>
                      </MenuItem>
                    </MenuItems>
                  )}
                </div>
              )}
            </Menu>
            {/* latest message: needs to be implemented */}
            <p className="cursor-pointer w-10/12 min-w-0 whitespace-nowrap overflow-hidden text-ellipsis block text-sm">
              user: this is placeholder text
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PrivateChatList;
